import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import styled from '@emotion/styled';
import SubMenu from '~/components/Chat/Chat01/SubMenu/SubMenu';
import { GroupChatSubMenu } from '~/components/ChatRoom';
import { Icon } from '~/components/atomic';
import {
  useSetNotificationMutation,
  useSetSafeChatMutation,
} from '~/features/chatRoom';
import { SetNotificationRequest, SetSafeChatRequest } from '~/types';
import { useCommonChatStore, useUserStore } from '~/store';
import Header from '../../../Header';
import { HeaderChatRoomProps } from '../../HeaderChatRoom';

interface RightMenuProps {
  type: HeaderChatRoomProps['type'];
  showCharacter: boolean;
  setShowCharacter: (showCharacter: boolean) => void;
  showSubMenu: boolean;
  setShowSubMenu: (showSubMenu: boolean) => void;
  showVideoCallModal: boolean;
  setShowVideoCallModal: (showVideoCallModal: boolean) => void;
  setIsReportOpen: (isReportOpen: boolean) => void;

  submenuRef: React.RefObject<HTMLDivElement>;
}

export default function RightMenu({
  type,
  showCharacter,
  setShowCharacter,
  showSubMenu,
  setShowSubMenu,
  submenuRef,
  setIsReportOpen,
}: RightMenuProps) {
  const { id: chatroom_id } = useParams();

  const email = useUserStore((state) => state.email);
  const {
    isNotificationOn,
    setIsNotificationOn,
    roomType: room_type,
    isSafeChatOn,
    setIsSafeChatOn,
  } = useCommonChatStore();
  const { mutate: setNotificationMutate, isPending: setNotificationPending } =
    useSetNotificationMutation();
  const { mutate: setSafeChatMutate, isError: setSafeChatError } =
    useSetSafeChatMutation();

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (!submenuRef.current) return;
      if (!submenuRef.current.contains(e.target as Node)) {
        setShowSubMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      setShowSubMenu(false);
    };
  }, []);

  const handleNotifyClick = async () => {
    if (!chatroom_id) return;
    if (!room_type) return;
    if (setNotificationPending) return;

    const newNotificationState = !isNotificationOn;
    const requestData: SetNotificationRequest = {
      chatroom_id,
      // room_type: room_type === 'group' ? 'group' : 'personal',
      notification: newNotificationState,
    };
    setNotificationMutate(requestData, {
      onSuccess({ status }) {
        setIsNotificationOn(newNotificationState);
      },
    });
  };

  const handleSafeModeClick = async () => {
    if (!chatroom_id) return;
    if (!room_type) return;

    if (
      room_type === 'group' ||
      !['mystery', 'concept', 'celeb', 'preset', 'user'].includes(room_type)
    )
      return;

    const newSafeModeOn = !isSafeChatOn;

    setIsSafeChatOn(newSafeModeOn);

    const requestData: SetSafeChatRequest = {
      chatroom_id,
      value: newSafeModeOn,
    };

    try {
      await setSafeChatMutate(requestData, {
        onError() {
          setIsSafeChatOn(!newSafeModeOn);
        },
      });
    } catch (error) {
      setIsSafeChatOn(!newSafeModeOn);
      console.error('Failed to update safe mode:', error);
    }
  };

  return (
    <>
      <Header.Dart />
      <ButtonBox>
        <Button onClick={() => setShowCharacter(!showCharacter)}>
          <Icon
            color='content_light_1'
            size={28}
            iconName={showCharacter ? 'UserDisable' : 'User'}
          />
        </Button>
      </ButtonBox>
      <ButtonBox ref={submenuRef}>
        <Button onClick={() => setShowSubMenu(!showSubMenu)}>
          <Icon color='content_light_1' size={28} iconName='MoreHorizontal' />
        </Button>
        {showSubMenu &&
          (type === 'chat' ? (
            <SubMenu
              isNotificationOn={isNotificationOn || false}
              handleNotifyClick={handleNotifyClick}
              isSafeChatOn={isSafeChatOn}
              handleSafeModeClick={handleSafeModeClick}
              handleReportClick={() => setIsReportOpen(true)}
            />
          ) : (
            <GroupChatSubMenu
              email={email}
              chatroomId={chatroom_id || ''}
              isNotificationOn={isNotificationOn || false}
              handleNotifyClick={handleNotifyClick}
              isSafeChatOn={isSafeChatOn}
              handleSafeModeClick={handleSafeModeClick}
              handleReportClick={() => setIsReportOpen(true)}
            />
          ))}
      </ButtonBox>
    </>
  );
}

export const ButtonBox = styled.div`
  position: relative;
  width: 44px;
`;

export const Button = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
`;
